/**
 * The review screen — the prototype's six-step wizard, made read-only.
 *
 * WHAT IS THE SAME: the header and its close button, the `.ft-wiz` two-column grid, the sticky dark
 * aside with its progress bars, the card's kicker / question / help stack, the Back + Continue
 * footer, and every breakpoint they move at.
 *
 * WHAT IS DIFFERENT, AND WHY: there is nothing to submit. v1's only write is "mark as given" for
 * worming and flea, and that lives on the reminders screen. So the wizard's "Confirm all as
 * correct" button and its "All confirmed" done card are gone rather than reworded — a control that
 * looks like it saves something and does not is worse than no control. What replaces them is a
 * jump-to-section list, which is navigation and therefore honest.
 *
 * THREE THINGS THAT WILL BITE A LATER EDIT
 * ----------------------------------------
 *  · `.ft-wiz` is a two-column grid above 1120px and must have EXACTLY TWO children — the aside
 *    and the card. A conditional wrapped in a `<div>` for convenience makes three and the layout
 *    silently collapses. Conditionals inside it must be fragments or nothing.
 *  · The section count is `STEP_COUNT`, never a literal. The prototype hard-coded "of 6" beside a
 *    step index it incremented without a bound, so a double-click printed "Step 7 of 6" over a
 *    blank body. `nextStep()` clamps and the label derives; keep both properties.
 *  · Every value on this screen comes from `DogPayload`. Writing a name, a date, a breed or an
 *    amount into this file re-creates the defect where one client saw another client's dog.
 */

import { useEffect, useRef, type CSSProperties, type ReactElement } from 'react'
import { fmtDay, parseDay, todayAtNoon } from '@ft/shared'

import { Icon, UI_ICON_PATHS } from '../model/icons'
import { STEP_COUNT, STEP_IDS, usePortal, useSelectedDog, type StepId } from '../state/portal'
import { REVIEW_SECTIONS } from './reviewSections'

const SCREEN_STYLE: CSSProperties = { animation: 'ft-rise .4s ease both' }

const HEADER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '14px',
  marginBottom: '20px',
}

const KICKER_STYLE: CSSProperties = {
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-700)',
  marginBottom: '7px',
}

const ASIDE_STYLE: CSSProperties = {
  background: 'var(--color-accent-2-800)',
  color: 'var(--color-bg)',
  borderRadius: 'calc(var(--radius-lg) * 1.15)',
  padding: '22px',
  boxShadow: 'var(--shadow-md)',
}

const ASIDE_RULE: CSSProperties = {
  marginTop: '18px',
  paddingTop: '16px',
  borderTop: '1px solid var(--color-accent-2-700)',
}

const ASIDE_MUTED: CSSProperties = {
  margin: '18px 0 0',
  fontSize: '12.5px',
  lineHeight: 1.55,
  color: 'var(--color-accent-2-200)',
}

/**
 * The jump buttons keep `.btn`'s padding, radius and — through the design system's global
 * `:focus-visible` — its focus ring, but reset the typeface the way every chip in the prototype
 * does. `.btn` uses the heading face (Caprasimo), which at 13px is unreadable; `font: inherit`
 * must come FIRST in the object because React writes these in insertion order and a shorthand
 * after a longhand would wipe it.
 */
const JUMP_BASE: CSSProperties = {
  font: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  width: '100%',
  minHeight: '40px',
  justifyContent: 'flex-start',
  textAlign: 'left',
  border: '1px solid transparent',
  background: 'transparent',
  color: 'var(--color-accent-2-200)',
}

const JUMP_ACTIVE: CSSProperties = {
  ...JUMP_BASE,
  fontWeight: 700,
  background: 'var(--color-accent-2-700)',
  borderColor: 'var(--color-accent-2-600)',
  color: 'var(--color-bg)',
}

/** Completed · current · still to read. Straight from the prototype (dc.html:907-909). */
function barBackground(index: StepId, step: StepId): string {
  if (index < step) return 'var(--color-accent-2-300)'
  if (index === step) return 'var(--color-accent-2-200)'
  return 'var(--color-accent-2-700)'
}

export function ReviewScreen(): ReactElement {
  const { state, me, go, goStep, nextStep, prevStep } = usePortal()
  const dog = useSelectedDog()

  const headingRef = useRef<HTMLHeadingElement>(null)
  const mountedRef = useRef(false)

  useEffect(() => {
    // Focus the section heading when the section CHANGES, so a screen-reader or keyboard user is
    // taken to the new content instead of being left at the bottom of the old one. Deliberately not
    // on first render: stealing focus from wherever the reader arrived from is disorienting, and
    // `preventScroll` keeps it from fighting the scroll-to-top that `nextStep()` already did.
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    headingRef.current?.focus({ preventScroll: true })
  }, [state.step])

  const heading = dog === null ? 'Review your record' : `Review ${dog.name}’s details`

  const header = (
    <header style={HEADER_STYLE}>
      <div>
        <div style={KICKER_STYLE}>Your record</div>
        <h1 style={{ margin: '0 0 7px', fontSize: 'clamp(30px, 5.5vw, 42px)' }}>{heading}</h1>
        <p className="text-muted" style={{ margin: 0, fontSize: '15px' }}>
          Everything we hold, in {STEP_COUNT} short sections. Nothing here changes unless you tell us.
        </p>
      </div>
      <button
        className="btn btn-secondary btn-icon"
        aria-label="Close review"
        onClick={() => go('home')}
        style={{ width: '44px', height: '44px', flex: 'none' }}
      >
        <Icon paths={UI_ICON_PATHS.close} size={17} />
      </button>
    </header>
  )

  // A failed first load must never render as "you have no dogs" — `me.data` stays null on failure,
  // which is exactly the state these branches separate.
  if (dog === null || me.data === null) {
    return (
      <section data-screen-label="Review" style={SCREEN_STYLE}>
        {header}
        <section className="card elev-sm" style={{ padding: '26px', gap: '10px' }} aria-busy={me.loading}>
          {me.loading ? (
            <p className="text-muted" style={{ margin: 0, fontSize: '14.5px' }}>
              Fetching your record…
            </p>
          ) : me.status === 'error' ? (
            <>
              <h2 style={{ margin: 0, fontSize: '20px' }}>We could not load your record</h2>
              <p className="text-muted" style={{ margin: 0, fontSize: '14px', lineHeight: 1.55 }}>
                {me.errorStatus === 401
                  ? 'Your sign-in has expired. Sign in again from the link we emailed you and this page will come back.'
                  : 'That is our end, not yours. Try again in a moment — and if it keeps happening, tell us and we will look.'}
              </p>
              <span>
                <button className="btn btn-primary" onClick={me.refresh} style={{ minHeight: '44px' }}>
                  Try again
                </button>
              </span>
            </>
          ) : (
            <>
              <h2 style={{ margin: 0, fontSize: '20px' }}>No dog on your record yet</h2>
              <p className="text-muted" style={{ margin: 0, fontSize: '14px', lineHeight: 1.55 }}>
                Your account is set up, but no dog is attached to it. Tell us at your next visit and we will link your
                dog’s record to this login.
              </p>
            </>
          )}
        </section>
      </section>
    )
  }

  const section = REVIEW_SECTIONS[state.step]
  const Body = section.Body
  const owner = me.data.owner
  // The server's date, not the device's. A wrong device clock must not be able to age a dog.
  const today = parseDay(me.data.serverDate) ?? todayAtNoon()

  const nextId: StepId | undefined = STEP_IDS[state.step + 1]
  const canBack = state.step > 0

  // "Contact review" is the record's own quarterly check-in item, and it is the only place on this
  // screen that says anything about when we will next ask. Absent for most dogs today — so it is
  // rendered only when the server actually produced it, never as a placeholder date.
  const checkIn = dog.care.find((item) => item.kind === 'contact')

  const updated = parseDay(dog.record.updatedAt)

  return (
    <section data-screen-label="Review" style={SCREEN_STYLE}>
      {header}

      {/* EXACTLY TWO CHILDREN — see the file header. */}
      <div className="ft-wiz">
        <aside className="ft-sticky" style={ASIDE_STYLE} aria-label="Review progress">
          <div
            style={{
              fontSize: '11px',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--color-accent-2-300)',
            }}
          >
            Profile review
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', marginTop: '6px' }}>{section.title}</div>
          <div style={{ fontSize: '13px', color: 'var(--color-accent-2-200)', marginTop: '4px' }}>
            Section {state.step + 1} of {STEP_COUNT}
          </div>

          {/* Decorative: the same information is in the count above and in the list below, and a
              screen reader announcing six unlabelled bars would be noise. */}
          <div aria-hidden="true" style={{ display: 'flex', gap: '6px', marginTop: '18px' }}>
            {STEP_IDS.map((id) => (
              <span
                key={id}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '999px',
                  background: barBackground(id, state.step),
                  transition: 'background .3s',
                }}
              />
            ))}
          </div>

          <p style={ASIDE_MUTED}>
            This is a read-through, not a form. Nothing on it can be changed here, so you cannot break anything by
            looking.
          </p>

          <nav style={ASIDE_RULE} aria-label="Review sections">
            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Jump to a section</div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '4px' }}>
              {STEP_IDS.map((id) => {
                const active = id === state.step
                return (
                  <li key={id}>
                    <button
                      className="btn btn-secondary"
                      // A token attribute, not a boolean: React drops a literal `false` and the
                      // "you are here" announcement would vanish with it.
                      aria-current={active ? 'step' : 'false'}
                      onClick={() => goStep(id)}
                      style={active ? JUMP_ACTIVE : JUMP_BASE}
                    >
                      {REVIEW_SECTIONS[id].title}
                    </button>
                  </li>
                )
              })}
            </ul>
          </nav>

          {checkIn === undefined ? null : (
            <div style={ASIDE_RULE}>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '4px' }}>Next check-in</div>
              <p style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.5, color: 'var(--color-accent-2-200)' }}>
                {checkIn.status}
                {checkIn.detail === '' ? '' : ` · ${checkIn.detail}`}
              </p>
            </div>
          )}
        </aside>

        <section className="card elev-sm" style={{ gap: 0, padding: '24px' }}>
          <div className="card-kicker" style={{ fontSize: '11px' }}>
            {section.title}
          </div>
          {/* h2, not the prototype's h3: the screen's h1 is the page title, and skipping a level
              breaks heading navigation for anyone browsing by structure. */}
          <h2
            ref={headingRef}
            tabIndex={-1}
            style={{ margin: '8px 0 6px', fontSize: 'clamp(21px, 3vw, 25px)', outlineOffset: '4px' }}
          >
            {section.question(dog.name)}
          </h2>
          <p className="text-muted" style={{ margin: '0 0 20px', fontSize: '14px' }}>
            {section.help(dog.name)}
          </p>

          <Body dog={dog} owner={owner} today={today} goReminders={() => go('reminders')} />

          {/* The honest affordance. It says what a client can actually do, which is not "edit". */}
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              marginTop: '22px',
              padding: '14px 16px',
              background: 'var(--color-neutral-100)',
              border: '1px solid var(--color-divider)',
              borderRadius: 'calc(var(--radius-lg) * 1.15)',
            }}
          >
            <Icon
              paths={UI_ICON_PATHS.message}
              size={16}
              stroke="var(--color-accent-700)"
              style={{ flex: 'none', marginTop: '2px' }}
            />
            <p className="text-muted" style={{ margin: 0, fontSize: '13px', lineHeight: 1.55 }}>
              Something wrong? Tell us at your next visit and we’ll update it.
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginTop: '22px',
              flexWrap: 'wrap',
            }}
          >
            <span
              className="text-muted"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px' }}
            >
              <Icon paths={UI_ICON_PATHS.check} size={14} stroke="var(--color-accent-2-600)" />
              {/* fmtDay renders an em dash for null, which would read as a date we are hiding —
                  hence the explicit branch rather than letting the formatter speak for absence. */}
              {updated === null ? 'Read-only — nothing here can be changed by mistake' : `Last updated ${fmtDay(updated)}`}
            </span>
            <span style={{ display: 'inline-flex', gap: '8px' }}>
              {canBack ? (
                <button className="btn btn-secondary" onClick={prevStep} style={{ minHeight: '44px' }}>
                  Back
                </button>
              ) : null}
              {nextId === undefined ? (
                <button className="btn btn-primary" onClick={() => go('home')} style={{ minHeight: '44px' }}>
                  Back to home
                  <Icon paths={UI_ICON_PATHS.arrowRight} size={15} />
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={nextStep}
                  // The visible word is inside the accessible name, so voice control still works.
                  aria-label={`Continue to ${REVIEW_SECTIONS[nextId].title}`}
                  style={{ minHeight: '44px' }}
                >
                  Continue
                  <Icon paths={UI_ICON_PATHS.arrowRight} size={15} />
                </button>
              )}
            </span>
          </div>
        </section>
      </div>
    </section>
  )
}
